import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * 全站唯一的「操作结果反馈」出口：弹窗。
 *
 * ★ 为什么必须是弹窗，而不是页面里的一条红字（2026-08-25 主公撞到）：
 *   投诉表单可滚 1218px、可视 628px，报错框挂在表单顶部；用户点「提交投诉」时人在底部，
 *   报错框落在可视区**上方 418px**——**渲染了但看不见**，现场表现就是「点了没反应」，
 *   只有控制台一条 400。滚动兜底（scrollIntoView）能救，但治标：只要页面够长，
 *   任何挂在固定位置的 inline 提示都会重演这个坑。
 *   所以改成绝对定位在 `.screen` 上的模态层——它不在滚动容器里，
 *   **无论用户滚到哪儿都在同一个位置出现**。
 *
 * 适用边界：
 *   · 弹窗＝「操作失败／操作结果」这类**结果反馈**（校验不过、提交失败、发码失败…）；
 *   · 空态文案（「这个手机号名下还没有投诉记录」）不是提示，**留在页面上**；
 *   · 页面级取数失败卡（带重试按钮的 ErrorState）是**页面状态**，也留在页面上。
 */

export interface AlertOptions {
  title: string;
  text?: string;
  /** 语气：fail＝朱色标题（默认），plain＝墨色标题 */
  tone?: 'fail' | 'plain';
}

type AlertFn = (opts: AlertOptions) => void;

const AlertCtx = createContext<{ show: AlertFn; current: AlertOptions | null; close: () => void }>({
  show: () => undefined,
  current: null,
  close: () => undefined,
});

export function AlertProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<AlertOptions | null>(null);
  const show = useCallback<AlertFn>((opts) => setCurrent(opts), []);
  const close = useCallback(() => setCurrent(null), []);
  const value = useMemo(() => ({ show, current, close }), [show, current, close]);
  return <AlertCtx.Provider value={value}>{children}</AlertCtx.Provider>;
}

/** 取弹窗函数：`const alert = useAlert(); alert({ title: '提交失败', text: 原因 })` */
export function useAlert(): AlertFn {
  return useContext(AlertCtx).show;
}

/**
 * 模态层。由 `Phone` 渲染在 `.screen` 内部（不是在 `.scroll` 里），
 * 所以它跟着屏幕走、不跟着内容滚。
 */
export function AlertLayer() {
  const { current, close } = useContext(AlertCtx);
  if (!current) return null;
  const tone = current.tone ?? 'fail';
  return (
    <div className="modal-mask" onClick={close} role="presentation">
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-label={current.title}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={tone === 'fail' ? 'fail' : undefined}>{current.title}</h3>
        {current.text ? <p>{current.text}</p> : null}
        <button type="button" className="btn btn-p modal-ok" onClick={close} autoFocus>
          知道了
        </button>
      </div>
    </div>
  );
}
